/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, UploadCloud, FileText, Search, X, Filter, Activity, RefreshCw, AlertTriangle, Trash2, Pencil
} from 'lucide-react';
import {
  knowledgeBaseService,
  AI_AGENT_ERROR_CODES,
  getApiErrorMessage,
  KB_DOCUMENT_FINAL_STATUSES as FINAL_STATUSES,
} from '../services/knowledgeBaseService';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import { parseTimestamp } from '../utils/agenticAiDates';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/knowledge-bases';
const AUTO_REFRESH_MS = 10000;
const MAX_AUTO_REFRESH_POLLS = 20;
const DOCUMENT_STATUSES = ['pending', 'extracting', 'chunking', 'embedding', 'processed', 'failed'];

const INITIAL_FILTERS = {
  search: '',
  status: 'all',
};

const formatDateTime = (value) => {
  const date = parseTimestamp(value);
  return date ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
};

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) { return '—'; }
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusLabel = (status) => (status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : 'Unknown');

const KnowledgeBaseDocuments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: knowledgeBaseId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canUploadDocument = can.knowledge_bases.upload();
  const canDeleteDocument = can.knowledge_bases.delete();
  // Editing metadata is part of the upload permission.
  const canEditDocument = canUploadDocument;
  const showActions = canEditDocument || canDeleteDocument;

  // Platform-wide view carries the account in the URL; account / sub-account users use their own.
  const showAccountField = !user?.accountUID;
  const accountId = showAccountField ? (searchParams.get('accountId') || '') : user?.accountUID;
  const accountQuery = showAccountField && accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';

  const [knowledgeBaseName, setKnowledgeBaseName] = useState(location.state?.knowledgeBase?.name || '');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  // Set when the Agentic AI platform itself does not respond properly.
  const [serviceError, setServiceError] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Bumped once per completed request (success or failure) to re-arm the poll timer.
  const [fetchCount, setFetchCount] = useState(0);
  const [pollLimitReached, setPollLimitReached] = useState(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (user && showAccountField && !accountId) {
      toastService.error(`Select ${ACCOUNT_LABEL.withArticle} first`);
      navigate(LIST_ROUTE, { replace: true });
    }
  }, [user, showAccountField, accountId, navigate]);

  const requestIdRef = useRef(0);
  const fetchDocuments = async ({ silent = false } = {}) => {
    if (!accountId) { return; }
    const currentRequestId = ++requestIdRef.current;
    try {
      if (silent) { setRefreshing(true); } else { setLoading(true); }
      const { data } = await knowledgeBaseService.getDocuments(
        knowledgeBaseId,
        showAccountField ? { accountId } : {}
      );
      if (currentRequestId !== requestIdRef.current) { return; }
      setIntegrationError(null);
      setServiceError(null);
      setDocuments(Array.isArray(data?.data) ? data.data : []);
      if (data?.knowledgeBase?.name) { setKnowledgeBaseName(data.knowledgeBase.name); }
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) { return; }
      const message = getApiErrorMessage(err, 'Failed to load documents');
      if (getAgenticAiServiceError(err)) {
        // Keep showing the last good list during background refreshes.
        if (!silent) {
          setServiceError(getAgenticAiServiceError(err));
          setDocuments([]);
        }
      } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
        setDocuments([]);
      } else if (!silent) {
        toastService.error(message);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
        setFetchCount((c) => c + 1);
      }
    }
  };

  const resetPolling = () => {
    pollCountRef.current = 0;
    setPollLimitReached(false);
  };

  useEffect(() => {
    resetPolling();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeBaseId, accountId]);

  // Keep polling while any document is still being processed: wait AUTO_REFRESH_MS after each
  // response before the next request (never overlapping), up to MAX_AUTO_REFRESH_POLLS requests.
  const hasInFlight = documents.some((d) => !FINAL_STATUSES.includes(d.status));
  useEffect(() => {
    if (!hasInFlight || loading || refreshing) { return undefined; }
    if (pollCountRef.current >= MAX_AUTO_REFRESH_POLLS) {
      setPollLimitReached(true);
      return undefined;
    }
    const timer = setTimeout(() => {
      pollCountRef.current += 1;
      fetchDocuments({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInFlight, loading, refreshing, fetchCount]);

  const handleManualRefresh = () => {
    resetPolling();
    fetchDocuments({ silent: true });
  };

  const filteredDocuments = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return documents.filter((d) => {
      if (filters.status !== 'all' && d.status !== filters.status) { return false; }
      if (!term) { return true; }
      const haystack = [d.name, ...Object.entries(d.tags || {}).map(([k, v]) => `${k} ${v}`)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [documents, filters]);

  const hasActiveFilters = Boolean(filters.search || filters.status !== 'all');
  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await knowledgeBaseService.deleteDocument(
        knowledgeBaseId,
        deleteTarget.id,
        showAccountField ? { accountId } : {}
      );
      toastService.success('Document deleted successfully');
      setDeleteTarget(null);
      fetchDocuments({ silent: true });
    } catch (err) {
      toastService.error(getApiErrorMessage(err, 'Failed to delete document'));
    } finally {
      setDeleting(false);
    }
  };

  const goToUpload = () => {
    navigate(`${LIST_ROUTE}/${encodeURIComponent(knowledgeBaseId)}/documents/new${accountQuery}`, {
      state: { knowledgeBase: { id: knowledgeBaseId, name: knowledgeBaseName } },
    });
  };

  const goToEdit = (doc) => {
    navigate(
      `${LIST_ROUTE}/${encodeURIComponent(knowledgeBaseId)}/documents/${encodeURIComponent(doc.id)}/edit${accountQuery}`,
      { state: { knowledgeBase: { id: knowledgeBaseId, name: knowledgeBaseName } } }
    );
  };

  const renderEmptyState = (title, description, action = null) => (
    <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
      <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
        <FileText size={28} />
      </div>
      <div>
        <h3 className="text-base font-black text-base-content">{title}</h3>
        <p className="text-xs text-base-content/40 max-w-sm mt-1">{description}</p>
      </div>
      {action}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="p-6 space-y-4">
          <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 bg-base-200/50 rounded-xl animate-pulse"></div>
            ))}
          </div>
        </div>
      );
    }
    if (serviceError) {
      return <AgenticAiUnavailable bare error={serviceError} onRetry={() => fetchDocuments()} />;
    }
    if (integrationError) {
      return (
        <div className="p-5 sm:p-6">
          <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
            <AlertTriangle size={18} className="shrink-0" />
            <span>{integrationError}</span>
          </div>
        </div>
      );
    }
    if (filteredDocuments.length === 0) {
      return hasActiveFilters
        ? renderEmptyState(
          'No matching documents',
          'Try adjusting your search query or reset filters.',
          <button
            type="button"
            onClick={handleClearFilters}
            className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
          >
            Clear all filters
          </button>
        )
        : renderEmptyState(
          'No documents uploaded yet',
          canUploadDocument
            ? 'Upload a PDF to make it available to the AI Agent.'
            : 'No documents have been added to this knowledge base.',
          canUploadDocument ? (
            <button
              type="button"
              onClick={goToUpload}
              className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
            >
              <UploadCloud size={14} />
              <span>Upload Document</span>
            </button>
          ) : null
        );
    }
    return (
      <div className="overflow-x-auto">
        <table className="table w-full text-left">
          <thead>
            <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
              <th className="py-3.5 px-5">Document</th>
              <th className="py-3.5 px-4">Metadata</th>
              <th className="py-3.5 px-4">Size</th>
              <th className="py-3.5 px-4 text-center">Status</th>
              <th className="py-3.5 px-5">Uploaded</th>
              {showActions && <th className="py-3.5 px-5 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-base-content/5 text-xs">
            {filteredDocuments.map((d) => {
              const tags = Object.entries(d.tags || {});
              const details = [d.fileType?.toUpperCase(), d.pageCount ? `${d.pageCount} page${d.pageCount === 1 ? '' : 's'}` : null]
                .filter(Boolean)
                .join(' · ');
              return (
                <tr key={d.id} className="hover:bg-base-200/40 transition-colors align-top">
                  {/* Document */}
                  <td className="py-3.5 px-5">
                    <div className="flex items-start gap-3 min-w-[12rem]">
                      <div className="w-9 h-9 rounded-xl bg-error/10 text-error flex items-center justify-center shrink-0">
                        <FileText size={18} />
                      </div>
                      <div className="min-w-0">
                        {d.publicLink ? (
                          <a
                            href={d.publicLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-sm text-base-content break-words hover:text-primary hover:underline"
                          >
                            {d.name}
                          </a>
                        ) : (
                          <p className="font-bold text-sm text-base-content break-words">{d.name}</p>
                        )}
                        {details && <p className="text-[11px] text-base-content/50 font-medium">{details}</p>}
                      </div>
                    </div>
                  </td>

                  {/* Metadata tags */}
                  <td className="py-3.5 px-4">
                    {tags.length === 0 ? (
                      <span className="text-base-content/40">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 max-w-md">
                        {tags.map(([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-base-200 text-base-content/70 border-base-content/5"
                          >
                            <span className="text-base-content/40">{key}:</span>
                            <span>{value}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Size */}
                  <td className="py-3.5 px-4 whitespace-nowrap">
                    <span className="text-base-content/70 font-medium">{formatFileSize(d.fileSize)}</span>
                  </td>

                  {/* Status */}
                  <td className="py-3.5 px-4 text-center">
                    <StatusBadge status={d.status || 'unknown'} />
                    {d.statusError && (
                      <p className="text-error text-[11px] font-medium mt-1 max-w-[14rem] mx-auto break-words">
                        {d.statusError}
                      </p>
                    )}
                  </td>

                  {/* Uploaded / processed */}
                  <td className="py-3.5 px-5 whitespace-nowrap">
                    <p className="text-base-content/70 font-medium">{formatDateTime(d.createdAt)}</p>
                    {d.processedAt && (
                      <p className="text-[11px] text-base-content/40">Processed {formatDateTime(d.processedAt)}</p>
                    )}
                  </td>

                  {/* Actions */}
                  {showActions && (
                    <td className="py-3.5 px-5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canEditDocument && (
                          <div
                            className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                            data-tip={FINAL_STATUSES.includes(d.status) ? 'Edit metadata' : 'Available once processing finishes'}
                          >
                            <button
                              type="button"
                              onClick={() => goToEdit(d)}
                              disabled={!FINAL_STATUSES.includes(d.status)}
                              className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all disabled:opacity-40 disabled:pointer-events-none"
                              aria-label="Edit Metadata"
                            >
                              <Pencil size={15} />
                            </button>
                          </div>
                        )}
                        {canDeleteDocument && (
                          <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete">
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(d)}
                              className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                              aria-label="Delete Document"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(`${LIST_ROUTE}${accountQuery}`)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Knowledge Base
        </button>
      </div>

      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
              Documents
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight break-words">
              {knowledgeBaseName || 'Knowledge Base'}
            </h1>
            <p className="text-xs text-base-content/50">
              Files already available to the AI Agent in this knowledge base.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={loading || refreshing || (Boolean(integrationError) || Boolean(serviceError))}
              className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-2 font-bold"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            {canUploadDocument && (
              <button
                type="button"
                onClick={goToUpload}
                disabled={(Boolean(integrationError) || Boolean(serviceError))}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <UploadCloud size={15} />
                <span>Upload Document</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className="relative col-span-12 sm:col-span-8 lg:col-span-9">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by file name or metadata..."
              disabled={(Boolean(integrationError) || Boolean(serviceError))}
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="col-span-12 sm:col-span-4 lg:col-span-3">
            <select
              aria-label="Status"
              disabled={(Boolean(integrationError) || Boolean(serviceError))}
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium disabled:opacity-50"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="all">All Statuses</option>
              {DOCUMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{statusLabel(s)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters((f) => ({ ...f, search: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Activity size={10} className="text-primary" />
                  <span>Status: {statusLabel(filters.status)}</span>
                  <button type="button" onClick={() => setFilters((f) => ({ ...f, status: 'all' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {renderBody()}

        {!loading && !integrationError && !serviceError && filteredDocuments.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap text-xs text-base-content/60 font-medium">
            <span>
              <span className="font-semibold text-base-content">{filteredDocuments.length}</span>
              {hasActiveFilters ? <> of <span className="font-semibold text-base-content">{documents.length}</span></> : null} documents
            </span>
            {hasInFlight && !pollLimitReached && (
              <span className="inline-flex items-center gap-1.5 text-base-content/50">
                <RefreshCw size={12} className="animate-spin" />
                Processing — refreshing automatically
              </span>
            )}
            {hasInFlight && pollLimitReached && (
              <span className="inline-flex items-center gap-1.5 text-base-content/50">
                <RefreshCw size={12} />
                Auto-refresh paused — click Refresh to check again
              </span>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) { setDeleteTarget(null); } }}
        title="Delete Document"
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200 break-all">
              {deleteTarget?.name}
            </strong>{' '}
            from <strong className="text-base-content">{knowledgeBaseName || 'this knowledge base'}</strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            The AI Agent will no longer be able to use this document. This action cannot be undone.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete Document'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeBaseDocuments;
